/**
 * El menú de cuenta del móvil · 13-10-2026
 *
 * En el móvil, la cabecera llevaba cuatro botones de texto —la luna, «Manual», «Mi
 * cuenta» y «Salir»— y el primer club lo resumió así: «demasiadas letras». Aquí van
 * juntos detrás de un círculo con tus iniciales, como en cualquier app. En el
 * ordenador siguen a la vista: hay sitio y se encuentran sin buscarlos.
 */
import { useCallback, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { elegirAspecto, useAspecto } from '@/shared/ui/aspecto';
import { BotonDelManual } from '@/shared/ui/BotonDelManual';
import { Icono } from '@/shared/ui/Icono';
import { useCerrarAlSalir } from '@/shared/ui/useCerrarAlSalir';

function iniciales(nombre: string | null | undefined, correo: string): string {
  const fuente = (nombre ?? '').trim() || correo;
  const partes = fuente.split(/[\s.@_-]+/).filter(Boolean);
  return ((partes[0]?.[0] ?? '') + (partes[1]?.[0] ?? '')).toUpperCase() || '·';
}

export function MenuDeCuenta({
  nombre,
  correo,
  manual,
  alSalir,
}: {
  nombre: string | null | undefined;
  correo: string;
  manual: 'club' | 'backoffice';
  alSalir: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const cerrar = useCallback(() => setAbierto(false), []);
  useCerrarAlSalir(abierto, caja, cerrar);
  const aspecto = useAspecto();
  const otro = aspecto === 'oscuro' ? 'claro' : 'oscuro';

  return (
    <div className="cuenta-caja" ref={caja}>
      <button
        type="button"
        className="avatar"
        aria-label="Tu cuenta"
        aria-expanded={abierto}
        aria-haspopup="menu"
        onClick={() => setAbierto((x) => !x)}
      >
        {iniciales(nombre, correo)}
      </button>

      {abierto && (
        <>
          <div className="hoja-fondo" aria-hidden="true" onClick={cerrar} />
          <div className="hoja hoja-menu" role="menu" aria-label="Tu cuenta">
            <div className="hoja-cabeza">
              <span className="cuenta-quien">
                <strong>{nombre?.trim() || 'Tu cuenta'}</strong>
                <span>{correo}</span>
              </span>
              <button
                type="button"
                className="btn-plano btn-icono"
                aria-label="Cerrar"
                onClick={cerrar}
              >
                <Icono nombre="cerrar" />
              </button>
            </div>
            <Link
              to="/mi-cuenta"
              className="menu-item"
              role="menuitem"
              onClick={cerrar}
            >
              <Icono nombre="cuenta" />
              Mi cuenta y contraseña
            </Link>
            <BotonDelManual de={manual} variante="menu" />
            <button
              type="button"
              className="menu-item"
              role="menuitem"
              onClick={() => elegirAspecto(otro)}
            >
              <Icono nombre={otro === 'oscuro' ? 'luna' : 'sol'} />
              {otro === 'oscuro' ? 'Pasar a modo oscuro' : 'Pasar a modo claro'}
            </button>
            <button
              type="button"
              className="menu-item menu-item-salir"
              role="menuitem"
              onClick={alSalir}
            >
              <Icono nombre="salir" />
              Salir
            </button>
          </div>
        </>
      )}
    </div>
  );
}
